import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { USE_REAL_API } from '@/services/api-client';
import * as msgApi from '@/services/messenger-api';

export type OnlineState = 'online' | 'away' | 'offline';

export interface ConvSummary {
  id: string;
  name: string;
  initials: string;
  avatarGradient: string;
  online: OnlineState;
  lastMessage: string;
  lastTime: string;
  unread: number;
}

export type ChatMessageType = 'TEXT' | 'IMAGE' | 'FILE' | 'LOCATION' | 'SYSTEM';

export interface ChatMessage {
  id: string;
  threadId: string;
  /** For TEXT: body. For IMAGE/FILE: url. For LOCATION: "lat,lng". */
  text: string;
  time: string;
  fromSelf: boolean;
  authorInitials: string;
  type?: ChatMessageType;
  fileName?: string;
  fileSize?: number;
  mimeType?: string;
}

const MOCK_CONVS: ConvSummary[] = [
  {
    id: 'c-zc',
    name: 'Zhong Chunhui',
    initials: 'ZC',
    avatarGradient: 'linear-gradient(135deg,#FF9500,#FFCC00)',
    online: 'online',
    lastMessage: 'Please review the Q2 production schedule before EOD',
    lastTime: '09:18',
    unread: 3,
  },
  {
    id: 'c-ch',
    name: 'Chen Hui',
    initials: 'CH',
    avatarGradient: 'linear-gradient(135deg,#4A1E8A,#6B3AAF)',
    online: 'online',
    lastMessage: 'Line 3 is back online, production resumed',
    lastTime: '08:55',
    unread: 1,
  },
];

const MOCK_MESSAGES: Record<string, ChatMessage[]> = {
  'c-zc': [
    { id: 'm-1', threadId: 'c-zc', text: 'Good morning! Have you had a chance to look at the Q2 production plan?', time: '08:41', fromSelf: false, authorInitials: 'ZC' },
    { id: 'm-2', threadId: 'c-zc', text: 'Good morning! Yes, just reviewing it now.', time: '08:43', fromSelf: true, authorInitials: 'SZ' },
    { id: 'm-3', threadId: 'c-zc', text: 'Please review the Q2 production schedule before EOD', time: '09:18', fromSelf: false, authorInitials: 'ZC' },
  ],
  'c-ch': [
    { id: 'm-c1', threadId: 'c-ch', text: 'Line 3 is back online, production resumed', time: '08:55', fromSelf: false, authorInitials: 'CH' },
  ],
};

let mockMsgSeq = 1000;

export function useConversations() {
  return useQuery({
    queryKey: ['messenger', 'convs', USE_REAL_API],
    queryFn: async () => {
      if (USE_REAL_API) return msgApi.fetchConversations();
      await new Promise((r) => setTimeout(r, 60));
      return MOCK_CONVS;
    },
    staleTime: 30_000,
  });
}

export function useChatMessages(threadId: string | null) {
  return useQuery({
    queryKey: ['messenger', 'msgs', threadId, USE_REAL_API],
    queryFn: async (): Promise<ChatMessage[]> => {
      if (!threadId) return [];
      if (USE_REAL_API) return msgApi.fetchMessages(threadId);
      await new Promise((r) => setTimeout(r, 50));
      return MOCK_MESSAGES[threadId] ?? [];
    },
    enabled: Boolean(threadId),
    staleTime: 30_000,
  });
}

export function useSendMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ threadId, text }: { threadId: string; text: string }) => {
      if (USE_REAL_API) return msgApi.sendMessage(threadId, text);
      await new Promise((r) => setTimeout(r, 100));
      const msg: ChatMessage = {
        id: `m-${mockMsgSeq++}`,
        threadId,
        text,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        fromSelf: true,
        authorInitials: 'SZ',
      };
      MOCK_MESSAGES[threadId] = [...(MOCK_MESSAGES[threadId] ?? []), msg];
      const conv = MOCK_CONVS.find((c) => c.id === threadId);
      if (conv) {
        conv.lastMessage = text;
        conv.lastTime = msg.time;
        conv.unread = 0;
      }
      return msg;
    },
    onSuccess: (msg) => {
      void qc.invalidateQueries({ queryKey: ['messenger', 'msgs', msg.threadId] });
      void qc.invalidateQueries({ queryKey: ['messenger', 'convs'] });
    },
  });
}

export function useSendRichMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: Parameters<typeof msgApi.sendRichMessage>) => {
      if (!USE_REAL_API) throw new Error('Rich send not supported in mock mode');
      return msgApi.sendRichMessage(args[0], args[1]);
    },
    onSuccess: (msg) => {
      void qc.invalidateQueries({ queryKey: ['messenger', 'msgs', msg.threadId] });
      void qc.invalidateQueries({ queryKey: ['messenger', 'convs'] });
    },
  });
}

export function useUploadFile() {
  return useMutation({
    mutationFn: (file: File) => msgApi.uploadFile(file),
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (threadId: string) => {
      if (USE_REAL_API) {
        await msgApi.markRead(threadId);
      } else {
        const conv = MOCK_CONVS.find((c) => c.id === threadId);
        if (conv) conv.unread = 0;
      }
      return threadId;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['messenger', 'convs'] });
    },
  });
}
